import { LogoMark } from '@repo/ui/src/components/LogoMark';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@repo/ui/src/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from '@repo/ui/src/components/ui/sidebar';
import {
  BadgePercent,
  Building2,
  ChevronsUpDown,
  Landmark,
  LogOut,
  Newspaper,
  ScrollText,
  Settings,
  Users,
} from 'lucide-react';
import { Link, Outlet, useLocation, useParams } from 'react-router';
import { useAdminOrg } from '../hooks/useAdminOrgs';
import { useAdminAuthStore } from '../stores/adminAuthStore';

const NAV = [
  { title: 'Tổ chức', href: '/orgs', icon: Building2 },
  { title: 'Người dùng', href: '/users', icon: Users },
  { title: 'Ưu đãi', href: '/promotions', icon: BadgePercent },
  { title: 'BankHub', href: '/bankhub', icon: Landmark },
  { title: 'Blog', href: '/blog', icon: Newspaper },
  { title: 'Cấu hình', href: '/config', icon: Settings },
  { title: 'Audit log', href: '/audit', icon: ScrollText },
];

/** The header bar's one job: say which ledger page you're on. `useParams`
 *  returns whatever `:id` the CURRENT route matched — /orgs/:id and /blog/:id
 *  both have one — so `useAdminOrg` must only fire on the org branch. Passing
 *  a blog post's id through to it would fire a stray GET /admin/orgs/:id (a
 *  guaranteed 404) every time an admin opens a post. */
function useBreadcrumb(): { section: string; detail: string | null } {
  const location = useLocation();
  const { id } = useParams<{ id?: string }>();
  const isOrgRoute = location.pathname.startsWith('/orgs');
  const { data: org } = useAdminOrg(isOrgRoute ? id : undefined);

  if (location.pathname.startsWith('/audit')) {
    return { section: 'Audit log', detail: null };
  }
  if (location.pathname.startsWith('/users')) {
    return { section: 'Người dùng', detail: null };
  }
  if (location.pathname.startsWith('/promotions')) {
    return { section: 'Ưu đãi', detail: null };
  }
  if (location.pathname.startsWith('/bankhub')) {
    return { section: 'BankHub', detail: null };
  }
  if (location.pathname.startsWith('/blog')) {
    return { section: 'Blog', detail: null };
  }
  if (location.pathname.startsWith('/config')) {
    return { section: 'Cấu hình', detail: null };
  }
  return { section: 'Tổ chức', detail: isOrgRoute && id ? (org?.name ?? null) : null };
}

/** Initials for the footer avatar: from the name when we have one, else the
 *  email's first letter. Never empty, so the block can't collapse. */
function initialsOf(fullName: string | null | undefined, email: string | undefined): string {
  const name = fullName?.trim();
  if (name) {
    return name
      .split(/\s+/)
      .map((w) => w[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }
  return (email?.[0] ?? '?').toUpperCase();
}

export function AdminLayout() {
  const location = useLocation();
  const user = useAdminAuthStore((s) => s.user);
  const clearAuth = useAdminAuthStore((s) => s.clearAuth);
  const { section, detail } = useBreadcrumb();
  const initials = initialsOf(user?.fullName, user?.email);

  return (
    <SidebarProvider>
      <Sidebar collapsible="icon">
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              {/* Not a switcher — there is only one platform. It says which app
                  you're in, which matters when the dashboard looks similar. */}
              <SidebarMenuButton size="lg" asChild>
                <Link to="/orgs">
                  {/* Wrapped: SidebarMenuButton's base style forces `[&>svg]:size-4`
                      on DIRECT svg children, which would shrink the mark to 16px.
                      The mark IS the rounded square (shadcn's sample nests a small
                      icon inside a coloured div instead), so it needs the full box. */}
                  {/* Collapsed, the base style forces the button to `!size-8`,
                      which is 25.6px here (--spacing is 0.2rem, not Tailwind's
                      0.25rem), so a fixed 32px mark gets clipped by the button's
                      overflow. size-full makes it fit whatever box it lands in. */}
                  <span className="flex size-10 shrink-0 items-center justify-center group-data-[collapsible=icon]:size-full">
                    <LogoMark className="size-full rounded-md" />
                  </span>
                  <div className="grid flex-1 text-left leading-tight group-data-[collapsible=icon]:hidden">
                    <span className="font-display truncate font-semibold text-surface-900">
                      LopHoc
                    </span>
                    <span className="truncate text-xs text-surface-500">Quản trị nền tảng</span>
                  </div>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarMenu>
              {NAV.map((item) => (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton
                    size="lg"
                    asChild
                    isActive={location.pathname.startsWith(item.href)}
                    tooltip={item.title}
                  >
                    <Link to={item.href}>
                      <item.icon />
                      {/* size="lg" keeps the button taller than the collapsed
                          rail's icon box, so the label isn't clipped away by
                          overflow — it leaks a sliver of its first letter into
                          the rail. Hide it outright when collapsed. */}
                      <span className="group-data-[collapsible=icon]:hidden">{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              {/* A menu, not a button: this used to log you out on a single
                  stray click, with no way back. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton size="lg" tooltip={user?.email ?? 'Tài khoản'}>
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-brand-100 text-[11px] font-semibold text-brand-800 group-data-[collapsible=icon]:size-full">
                      {initials}
                    </span>
                    <div className="grid flex-1 text-left leading-tight group-data-[collapsible=icon]:hidden">
                      <span className="truncate font-medium text-surface-900">
                        {user?.fullName ?? 'Quản trị viên'}
                      </span>
                      <span className="truncate text-xs text-surface-500">{user?.email}</span>
                    </div>
                    <ChevronsUpDown className="ml-auto size-4 text-surface-400 group-data-[collapsible=icon]:hidden" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="start" className="w-56">
                  <DropdownMenuItem onClick={clearAuth} className="cursor-pointer">
                    <LogOut className="mr-2 size-4" />
                    Đăng xuất
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
      <SidebarInset>
        <header className="flex h-12 shrink-0 items-center gap-3 border-b border-surface-200 px-4">
          <SidebarTrigger />
          <div className="h-4 w-px bg-surface-200" aria-hidden="true" />
          <div className="flex items-center gap-1.5 text-sm leading-none">
            <span className="text-surface-500">{section}</span>
            {detail && (
              <>
                <span className="text-surface-300">/</span>
                <span className="font-display font-medium text-surface-900">{detail}</span>
              </>
            )}
          </div>
        </header>
        <div className="flex-1 overflow-auto p-6">
          <div className="mx-auto w-full max-w-6xl">
            <Outlet />
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
